/**
 * Tote Service Client
 *
 * Calls WHSEAPService's /tote/* endpoints which proxy to Austin's
 * WADM Tote Management API (or the mock client in local/beta environments).
 */

import apiClient from "src/utils/apiClient";
import {
  GetToteByScannableIdResponse,
  GetItemsInToteRequest,
  GetItemsInToteResponse,
  GetItemsManifestResponse,
  MoveItemsToContainerInput,
  MoveItemsToContainerResponse,
  ReportMissingItemsInput,
  ReportMissingItemsResponse,
  MoveToteItem,
  ToteItem,
} from "src/types/toteTypes";
import { WadmContainer } from "src/types/containerTypes";

/**
 * GET /tote/{scannable_id}?site_code=
 * Resolves a tote barcode to its numeric containerId.
 */
export const getToteByScannableId = async (
  scannableId: string,
  siteCode: string,
): Promise<GetToteByScannableIdResponse> => {
  const response = await apiClient.get<GetToteByScannableIdResponse>(
    `/tote/${encodeURIComponent(scannableId)}`,
    {
      params: { site_code: siteCode },
    },
  );
  return response.data;
};

/**
 * POST /tote/{scannable_id}/items
 * Fetches items in a tote. Item IDs sent in the request body to avoid URL length limits.
 */
export const getItemsInTote = async (
  scannableId: string,
  payload: GetItemsInToteRequest,
): Promise<GetItemsInToteResponse> => {
  const response = await apiClient.post<GetItemsInToteResponse | ToteItem[]>(
    `/tote/${encodeURIComponent(scannableId)}/items`,
    payload,
  );

  const data = response.data;
  if (Array.isArray(data)) {
    return { items: data, uniqueItemCount: data.length, type: "TOTE" };
  }
  return data;
};

/**
 * GET /tote/{scannable_id}/items/manifest?tote_container_id=
 * Returns the full list of available and unavailable item IDs for a tote.
 */
export const getItemsManifest = async (params: {
  scannableId: string;
  siteCode: string;
  toteContainerId: number;
}): Promise<GetItemsManifestResponse> => {
  const { scannableId, siteCode, toteContainerId } = params;

  const response = await apiClient.get<GetItemsManifestResponse>(
    `/tote/${encodeURIComponent(scannableId)}/items/manifest`,
    { params: { tote_container_id: toteContainerId, site_code: siteCode } },
  );
  return response.data;
};

/**
 * POST /tote/items/move
 * Moves selected tote items into a WADM waste container.
 */
export const moveItemsToContainer = async (
  payload: MoveItemsToContainerInput,
): Promise<MoveItemsToContainerResponse> => {
  const response = await apiClient.post<MoveItemsToContainerResponse>("/tote/items/move", payload);
  return response.data;
};

export const reportMissingItems = async (
  payload: ReportMissingItemsInput,
): Promise<ReportMissingItemsResponse> => {
  const response = await apiClient.post<ReportMissingItemsResponse>("/tote/items/missing", payload);
  return response.data;
};

/**
 * Helper: fetches all items in a tote via the 3-step flow:
 * 1. Resolve scannableId → toteContainerId
 * 2. Warm-up call — mirrors Austin's frontend behavior (see note below)
 * 3. Get manifest → availableIds
 * 4. Fetch items using those IDs
 *
 * Note on the warm-up call (step 2):
 * Austin's frontend (ToteEditor.tsx) makes a getItemsInTote() call with
 * firstPageLoad=true and pageSize=1 before fetching the manifest. We mirror
 * this behavior to maintain parity with their implementation.
 * Reference to Austin FE code: https://code.amazon.com/packages/AustinEhsWasteAndDonationsFrontend/blobs/02435acbcbfb9dd225ba311e6e5947bb054a3357/--/src/modules/tote-scanning/ToteEditor.tsx#L625,L620,L621,L622,L623
 */
export const fetchToteItems = async (
  scannableId: string,
  siteCode: string,
): Promise<{ items: ToteItem[]; toteContainerId: number }> => {
  const toteResponse = await getToteByScannableId(scannableId, siteCode);
  const toteContainerId = toteResponse.containerId;

  if (toteContainerId === undefined || toteContainerId === null) {
    throw new Error("Tote not found for this site.");
  }

  // Warm-up call — mirrors Austin's frontend behavior. Response is discarded.

  await getItemsInTote(scannableId, {
    siteCode,
    toteContainerId,
    firstPageLoad: true,
    pageSize: 1,
    itemIds: [],
  });

  const manifest = await getItemsManifest({ scannableId, siteCode, toteContainerId });
  const itemsResponse = await getItemsInTote(scannableId, {
    siteCode,
    toteContainerId,
    firstPageLoad: false,
    itemIds: manifest.availableIds,
  });

  return { items: itemsResponse.items, toteContainerId };
};

/**
 * Converts a ToteItem to a MoveToteItem for the moveItemsToContainer API.
 *
 * Mirrors Austin's ToteMoveItemConfirmationModal.formToMoveToteItems logic:
 
 * Reference: https://code.amazon.com/packages/AustinEhsWasteAndDonationsFrontend/blobs/mainline/--/src/modules/tote-scanning/components/ToteMoveItemConfirmationModal.tsx#L903
 */
export const mapToMoveToteItem = (
  item: ToteItem,
  scannableId: string,
  container: WadmContainer | undefined,
  overriddenQuantity?: number,
  overriddenWeight?: number,
): MoveToteItem => {
  const quantity = overriddenQuantity ?? item.quantity;
  const weight = overriddenWeight ?? quantity * (item.weight?.value ?? 0);
  const unit = container?.maxWeight?.unit ?? "POUND";
  const usingFor = container?.usingFor;

  return {
    id: item.id,
    fcSku: item.fcSku,
    disposition: item.disposition,
    consumerType: item.consumerType,
    consumerReferenceId: item.consumerReferenceId,
    asin: item.asin,
    name: item.name ?? "",
    marketplaceId: item.marketplaceId,
    siteId: item.siteId,
    siteName: item.siteName,
    toteContainerId: item.toteContainerId,
    toteScannableId: scannableId,
    quantity,
    isInventory: true,
    isManuallyAdded: false,
    ...(container && { totalWeight: { weight, unit } }),
    processingAttribute: item.processingAttribute,
    ...(usingFor === "WASTE" &&
      container && {
        profileId: container.wasteProfileId,
        isProfileOverridden: container.wasteProfileId
          ? !item.profileIds?.includes(container.wasteProfileId)
          : false,
        recommendedProfiles: item.profileIds,
      }),
    ...(usingFor === "DONATION" &&
      container && {
        categoryId: container.donationCategoryId,
        isCategoryOverridden: container.donationCategoryId
          ? !item.recommendedCategories?.map((c) => c.id).includes(container.donationCategoryId)
          : false,
        recommendedCategories: item.recommendedCategories?.map((c) => c.id),
      }),
  };
};
